import React, { useEffect, useMemo, useState } from 'react'
import { X, Play, Search } from 'lucide-react'
import './HelpCenter.css'

// Walk a React node tree and return the concatenated plain-text so
// section bodies become searchable without turning each one into a
// hand-written keyword string.
function extractText(node) {
  if (node == null || typeof node === 'boolean') return ''
  if (typeof node === 'string' || typeof node === 'number') return String(node)
  if (Array.isArray(node)) return node.map(extractText).join(' ')
  if (node.props && node.props.children) return extractText(node.props.children)
  return ''
}

/**
 * Map of walkthrough-key → ordered step list. The key must match the
 * `slug` of a Help Center section; when present, that section gets a
 * play-icon button next to its title that launches the matching
 * walkthrough. Sections without an entry here show no play button.
 */
// Shared helper: click an element matched by selector, then (after the
// next frame) click a second one. Used by multi-step opener flows like
// the announcement composer, which routes through a type-selector modal
// before the fields walkthrough-step 3+ wants to spotlight exist.
const chainClick = (firstSel, secondSel) => {
  document.querySelector(firstSel)?.click()
  setTimeout(() => {
    document.querySelector(secondSel)?.click()
  }, 60)
}

export const walkthroughs = {
  'your-profile': [
    {
      target: '[data-walk="user-menu-btn"]',
      title: 'Open your account menu',
      body: 'Click your avatar in the top-right to open your account menu.',
      placement: 'left',
      onNext: () => { document.querySelector('[data-walk="user-menu-btn"]')?.click() },
    },
    {
      target: '[data-walk="user-menu-profile"]',
      title: 'Go to Profile',
      body: 'Click Profile to open your settings.',
      placement: 'left',
      onNext: () => { document.querySelector('[data-walk="user-menu-profile"]')?.click() },
    },
    {
      target: '[data-walk="profile-header"]',
      title: 'Your profile',
      body: 'Here you can update your name, photo, address, phone, email, and notification preferences. Changes autosave as you type.',
      placement: 'bottom',
      waitForNav: '/profile',
    },
  ],
  'finding-your-neighbors': [
    {
      target: '[data-walk="directory-nav"]',
      title: 'Open Directory',
      body: 'Click Directory in the sidebar to see all your neighbors.',
      placement: 'right',
    },
    {
      target: '[data-walk="directory-search"]',
      title: 'Search neighbors',
      body: 'Search by name or address to find someone fast.',
      placement: 'bottom',
      waitForNav: '/directory',
    },
    {
      target: '[data-walk="directory-message-btn"]',
      title: 'Start a conversation',
      body: 'Click the chat bubble to start a conversation with a neighbor.',
      placement: 'left',
    },
  ],
  'reading-announcements': [
    {
      target: '[data-walk="announcements-nav"]',
      title: 'Open Announcements',
      body: 'The Announcements tab is where your board and admins post neighborhood updates and polls.',
      placement: 'right',
    },
    {
      target: '[data-walk="announcement-card-first"]',
      title: 'Read an announcement',
      body: 'Click any announcement to open it. Links inside posts are clickable, and admins can edit or delete them.',
      placement: 'left',
      waitForNav: '/announcements',
    },
  ],
  'events': [
    {
      target: '[data-walk="events-nav"]',
      title: 'Open Events',
      body: "The Events tab shows what's coming up in your neighborhood.",
      placement: 'right',
    },
    {
      target: '[data-walk="events-view-toggle"]',
      title: 'Calendar or List',
      body: 'Switch between Calendar and List views depending on how you like to browse.',
      placement: 'bottom',
      waitForNav: '/events',
    },
  ],
  'messages': [
    {
      target: '[data-walk="messages-nav"]',
      title: 'Open your inbox',
      body: 'Click the chat icon anytime to open your inbox.',
      placement: 'bottom',
      onNext: () => { document.querySelector('[data-walk="messages-nav"]')?.click() },
    },
    {
      target: '[data-walk="messages-tabs"]',
      title: 'Inbox vs Archived',
      body: "Switch between Inbox and Archived. Archive a conversation from its ··· menu — archived threads don't count toward the unread badge.",
      placement: 'bottom',
      waitForNav: '/messages',
    },
  ],
  'blocking-a-neighbor': [
    {
      target: '[data-walk="discussions-nav"]',
      title: 'Open Discussions',
      body: 'The Block / Hide controls live on the ⋯ menu of each discussion. Head to Discussions first.',
      placement: 'right',
    },
    {
      target: '[data-walk="discussion-card-first"]',
      title: 'Open any discussion',
      body: 'Click any discussion to open it. On the detail page, the author\'s neighbor controls appear in the top-right.',
      placement: 'left',
      waitForNav: '/discussions',
    },
    {
      target: '[data-walk="discussion-menu-btn"]',
      title: 'Open the ⋯ menu',
      body: 'Click the ⋯ in the top-right of the discussion card. You\'ll see Flag, Hide neighbor, and (for repeat problem users) Block.',
      placement: 'left',
    },
    {
      target: '[data-walk="user-menu-btn"]',
      title: 'Managing blocks',
      body: 'Later you can review or undo blocks from your account menu → Blocked Users.',
      placement: 'left',
    },
  ],
  'settings': [
    {
      target: '[data-walk="settings-btn"]',
      title: 'Open Settings',
      body: 'Click the gear to open your personal settings.',
      placement: 'bottom',
      onNext: () => { document.querySelector('[data-walk="settings-btn"]')?.click() },
    },
    {
      target: '[data-walk="settings-appearance"]',
      title: 'Appearance',
      body: 'Choose Light, Dark, or System for your preferred color theme.',
      placement: 'left',
    },
  ],
  'admin-panel-overview': [
    {
      target: '[data-walk="admin-nav"]',
      title: 'Open Admin',
      body: 'Click Admin in the sidebar to open your admin console.',
      placement: 'right',
    },
    {
      target: '[data-walk="admin-sidebar"]',
      title: 'Admin menu',
      body: 'Everything you manage is listed here — Neighborhood Profile, Households, Flagged Content, Discussions, Categories, Announcements, Events, HOA Payment Tracker, and Subscription.',
      placement: 'right',
      waitForNav: '/admin',
    },
  ],
  'editing-neighborhood-profile': [
    {
      target: '[data-walk="admin-nav"]',
      title: 'Open Admin',
      body: 'Open the Admin tab.',
      placement: 'right',
    },
    {
      target: '[data-walk="admin-menu-neighborhood-profile"]',
      title: 'Neighborhood Profile',
      body: "Click Neighborhood Profile. This is where the neighborhood's name, bio, logo, board, and admin lists live.",
      placement: 'right',
      waitForNav: '/admin',
      onNext: () => { document.querySelector('[data-walk="admin-menu-neighborhood-profile"]')?.click() },
    },
    {
      target: '[data-walk="np-bio"]',
      title: 'Neighborhood Bio',
      body: 'Set a short description of your neighborhood. It shows up in the footer for every resident.',
      placement: 'top',
    },
  ],
  'adding-households': [
    {
      target: '[data-walk="admin-nav"]',
      title: 'Open Admin',
      body: 'Open Admin.',
      placement: 'right',
    },
    {
      target: '[data-walk="admin-menu-households"]',
      title: 'Manage Households',
      body: 'Click Manage Households.',
      placement: 'right',
      waitForNav: '/admin',
      onNext: () => { document.querySelector('[data-walk="admin-menu-households"]')?.click() },
    },
    {
      target: '[data-walk="households-add-btn"]',
      title: 'Add a household',
      body: 'Click here to add a new household. Enter the address and save — the system creates a pending slot waiting for someone to register.',
      placement: 'left',
    },
  ],
  'generating-invitation-codes': [
    {
      target: '[data-walk="admin-nav"]',
      title: 'Open Admin',
      body: 'Open Admin.',
      placement: 'right',
    },
    {
      target: '[data-walk="admin-menu-households"]',
      title: 'Manage Households',
      body: 'Click Manage Households.',
      placement: 'right',
      waitForNav: '/admin',
      onNext: () => { document.querySelector('[data-walk="admin-menu-households"]')?.click() },
    },
    {
      target: '[data-walk="households-pending-filter"]',
      title: 'Filter to pending',
      body: 'Switch to Inactive to see households without a registered resident yet — pending and vacant appear here.',
      placement: 'bottom',
    },
    {
      target: '[data-walk="generate-code-btn"]',
      title: 'Generate a code',
      body: "You'll see a Generate Code button next to each pending household. Click it to generate a unique 6-character code and share it with the resident.",
      placement: 'left',
    },
  ],
  'emailing-invitations': [
    {
      target: '[data-walk="admin-nav"]',
      title: 'Open Admin',
      body: 'Open Admin.',
      placement: 'right',
    },
    {
      target: '[data-walk="admin-menu-households"]',
      title: 'Manage Households',
      body: 'Click Manage Households.',
      placement: 'right',
      waitForNav: '/admin',
      onNext: () => { document.querySelector('[data-walk="admin-menu-households"]')?.click() },
    },
    {
      target: '[data-walk="email-invitations-btn"]',
      title: 'Email invitations',
      body: 'Click Email Invitations to open the bulk email tool.',
      placement: 'left',
    },
  ],
  'exporting-mailing-labels': [
    {
      target: '[data-walk="admin-nav"]',
      title: 'Open Admin',
      body: 'Open Admin.',
      placement: 'right',
    },
    {
      target: '[data-walk="admin-menu-households"]',
      title: 'Manage Households',
      body: 'Click Manage Households.',
      placement: 'right',
      waitForNav: '/admin',
      onNext: () => { document.querySelector('[data-walk="admin-menu-households"]')?.click() },
    },
    {
      target: '[data-walk="export-labels-btn"]',
      title: 'Export mailing labels',
      body: 'Click Export mailing labels to generate a printable PDF with a postcard template plus Avery 5160 labels.',
      placement: 'left',
    },
  ],
  'posting-announcement': [
    {
      target: '[data-walk="announcements-nav"]',
      title: 'Open Announcements',
      body: 'Open the Announcements tab.',
      placement: 'right',
    },
    {
      target: '[data-walk="new-announcement-btn"]',
      title: 'Start a new announcement',
      body: 'Click + to open the announcement composer.',
      placement: 'left',
      waitForNav: '/announcements',
      // Clicking + opens the type-selector modal. Chain-click the
      // Announcement option so the next step's targets (inside
      // CreateAnnouncementModal) actually exist.
      onNext: () => chainClick(
        '[data-walk="new-announcement-btn"]',
        '[data-walk="type-selector-announcement"]'
      ),
    },
    {
      target: '[data-walk="announcement-title-input"]',
      title: 'Add a title',
      body: 'Enter a short title.',
      placement: 'bottom',
    },
    {
      target: '[data-walk="announcement-content-textarea"]',
      title: 'Write the content',
      body: 'Write the content. Use the link icon above to add clickable hyperlinks.',
      placement: 'top',
    },
    {
      target: '[data-walk="announcement-email-checkbox"]',
      title: 'Email neighbors',
      body: "Leave 'Email all neighbors' checked to email everyone, or uncheck to keep it in-app only.",
      placement: 'top',
    },
    {
      target: '[data-walk="announcement-submit-btn"]',
      title: 'Post it',
      body: 'Click Post Announcement to publish.',
      placement: 'top',
    },
  ],
  'managing-discussions': [
    {
      target: '[data-walk="admin-nav"]',
      title: 'Open Admin',
      body: 'Open Admin.',
      placement: 'right',
    },
    {
      target: '[data-walk="admin-menu-discussions"]',
      title: 'Manage Discussions',
      body: 'Click Manage Discussions to see every discussion in your neighborhood.',
      placement: 'right',
      waitForNav: '/admin',
      onNext: () => { document.querySelector('[data-walk="admin-menu-discussions"]')?.click() },
    },
  ],
  'categories': [
    {
      target: '[data-walk="admin-nav"]',
      title: 'Open Admin',
      body: 'Open Admin.',
      placement: 'right',
    },
    {
      target: '[data-walk="admin-menu-categories"]',
      title: 'Manage Categories',
      body: 'Click Manage Categories to add or remove the tags neighbors can use when posting.',
      placement: 'right',
      waitForNav: '/admin',
      onNext: () => { document.querySelector('[data-walk="admin-menu-categories"]')?.click() },
    },
    {
      target: '[data-walk="category-add-input"]',
      title: 'Add a category',
      body: 'Type a category name here, then press Enter or click Add.',
      placement: 'bottom',
    },
  ],
  'creating-events': [
    {
      target: '[data-walk="admin-nav"]',
      title: 'Open Admin',
      body: 'Open Admin.',
      placement: 'right',
    },
    {
      target: '[data-walk="admin-menu-events"]',
      title: 'Manage Events',
      body: 'Click Manage Events.',
      placement: 'right',
      waitForNav: '/admin',
      onNext: () => { document.querySelector('[data-walk="admin-menu-events"]')?.click() },
    },
    {
      target: '[data-walk="event-add-btn"]',
      title: 'Add an event',
      body: 'Click + Add event to open the event form.',
      placement: 'left',
    },
  ],
  'hoa-payment-tracker': [
    {
      target: '[data-walk="admin-nav"]',
      title: 'Open Admin',
      body: 'Open Admin.',
      placement: 'right',
    },
    {
      target: '[data-walk="admin-menu-hoa"]',
      title: 'HOA Payment Tracker',
      body: 'Click HOA Payment Tracker.',
      placement: 'right',
      waitForNav: '/admin',
      onNext: () => { document.querySelector('[data-walk="admin-menu-hoa"]')?.click() },
    },
    {
      target: '[data-walk="hoa-config"]',
      title: 'Configure your fee',
      body: 'Set your fee amount, due dates, and fiscal year here. Then track which households have paid for each period.',
      placement: 'top',
    },
  ],
  'subscription': [
    {
      target: '[data-walk="admin-nav"]',
      title: 'Open Admin',
      body: 'Open Admin.',
      placement: 'right',
    },
    {
      target: '[data-walk="admin-menu-subscription"]',
      title: 'Subscription',
      body: 'Click Subscription to see your plan, next billing date, and payment method.',
      placement: 'right',
      waitForNav: '/admin',
      onNext: () => { document.querySelector('[data-walk="admin-menu-subscription"]')?.click() },
    },
  ],
  'posting-a-discussion': [
    {
      target: '[data-walk="discussions-nav"]',
      title: 'Open Discussions',
      body: 'Click the Discussions tab in the left sidebar. This is where everyone in your neighborhood posts topics, questions, and polls.',
      placement: 'right',
    },
    {
      target: '[data-walk="new-discussion-btn"]',
      title: 'Start a new post',
      body: 'Click the + button to open the new-discussion form.',
      placement: 'left',
      waitForNav: '/discussions',
      // Advancing from this step should also open the modal so the
      // next step can spotlight the Title input inside it.
      onNext: () => {
        document.querySelector('[data-walk="new-discussion-btn"]')?.click()
      },
    },
    {
      target: '[data-walk="discussion-title-input"]',
      title: 'Add a title',
      body: 'Give your discussion a clear, descriptive title.',
      placement: 'bottom',
    },
    {
      target: '[data-walk="discussion-category-select"]',
      title: 'Pick a category',
      body: 'Pick a category so neighbors can filter by topic.',
      placement: 'bottom',
    },
    {
      target: '[data-walk="discussion-content-textarea"]',
      title: 'Write your post',
      body: 'Write your post. You can select text and click the link icon above to add a hyperlink.',
      placement: 'top',
    },
    {
      target: '[data-walk="discussion-submit-btn"]',
      title: 'Post it',
      body: "When you're ready, click Post to publish. Your neighbors will see it right away.",
      placement: 'top',
    },
  ],
}

/**
 * Full-screen Help Center modal with a sticky TOC on the left and
 * scrollable long-form content on the right. Content is hard-coded
 * as structured JSX — no CMS. The admin bucket is only rendered for
 * users with an admin / board / super-admin flag on their profile.
 */
function HelpCenter({ isOpen, onClose, userProfile, onStartWalkthrough }) {
  const isAdmin = !!(userProfile?.isAdmin || userProfile?.isBoardMember || userProfile?.isSuperAdmin)
  const [query, setQuery] = useState('')

  // Close on ESC. Attached only while open so we don't leak listeners.
  useEffect(() => {
    if (!isOpen) return
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isOpen, onClose])

  // Reset the search query every time the modal reopens so stale
  // filters don't hide content for the next visit.
  useEffect(() => {
    if (isOpen) setQuery('')
  }, [isOpen])

  // Bucket definitions. Each section has a slug (used as the id anchor
  // and as the href target in the TOC), a title, and a content node.
  const buckets = useMemo(() => {
    const gettingStarted = {
      key: 'getting-started',
      title: 'Getting started',
      sections: [
        {
          slug: 'signing-in',
          title: 'Signing in',
          body: (
            <p>
              Use your email + password at <code>portal.neighbortopia.app</code>. First-time users enter the
              invitation code they received by mail or email, then set a password.
            </p>
          ),
        },
        {
          slug: 'your-profile',
          title: 'Your profile',
          body: (
            <p>
              Click the avatar in the top-right &rarr; Profile. You can update your name, photo, address,
              phone, email, and notification preferences. Changes save automatically.
            </p>
          ),
        },
        {
          slug: 'finding-your-neighbors',
          title: 'Finding your neighbors',
          body: (
            <p>
              The Directory tab lists everyone in your neighborhood. Use the search box or filters to find a
              specific person. Click the message icon on a row to start a conversation.
            </p>
          ),
        },
        {
          slug: 'posting-a-discussion',
          title: 'Posting a discussion',
          body: (
            <p>
              Discussions tab &rarr; click the + button. Give it a title, pick a category, write your post,
              and click Post.
            </p>
          ),
        },
        {
          slug: 'replying-edit-history',
          title: 'Replying & edit history',
          body: (
            <p>
              Click any discussion to open it. Add a reply at the bottom. If you edit a reply later, neighbors
              can tap &ldquo;Edited &middot; view history&rdquo; to see earlier versions &mdash; a transparency
              feature so edits don&rsquo;t look sneaky.
            </p>
          ),
        },
        {
          slug: 'reading-announcements',
          title: 'Reading announcements',
          body: (
            <p>
              The Announcements tab is where board members and admins post neighborhood updates and polls. New
              posts also show up on the Home dashboard.
            </p>
          ),
        },
        {
          slug: 'events',
          title: 'Events',
          body: (
            <p>
              Events tab shows upcoming neighborhood events in a calendar or list view. Admins create events;
              everyone else can view them.
            </p>
          ),
        },
        {
          slug: 'messages',
          title: 'Messages',
          body: (
            <p>
              Click the chat bubble icon in the top-right. Start a conversation with any neighbor from the
              Directory. You can archive conversations you no longer need, and mark threads as unread to flag
              them.
            </p>
          ),
        },
        {
          slug: 'blocking-a-neighbor',
          title: 'Blocking a neighbor',
          body: (
            <p>
              On any discussion or message, use the &middot;&middot;&middot; menu &rarr; Block user. Blocked
              users can no longer message you or appear in your Directory.
            </p>
          ),
        },
        {
          slug: 'settings',
          title: 'Settings',
          body: (
            <p>
              Click the gear icon (top-right) to change appearance (light/dark/system), time zone, time
              format, compact mode, and your default landing page.
            </p>
          ),
        },
      ],
    }

    const adminBucket = {
      key: 'admins',
      title: 'For admins',
      sections: [
        {
          slug: 'admin-panel-overview',
          title: 'Admin panel overview',
          body: (
            <p>
              The Admin tab is where you manage your neighborhood. The left sidebar lists every section:
              Neighborhood Profile, Manage Households, Flagged Content, Discussions, Categories,
              Announcements, Events, HOA Payment Tracker, Subscription.
            </p>
          ),
        },
        {
          slug: 'editing-neighborhood-profile',
          title: 'Editing the neighborhood profile',
          body: (
            <p>
              Admin &rarr; Neighborhood Profile. Set the neighborhood name, bio, logo (uploads via image
              cropper), board members, and neighborhood admins. Board Members and Neighborhood Admins are
              auto-populated from users flagged as such in the Directory &mdash; add titles like
              &ldquo;President&rdquo; or &ldquo;Treasurer&rdquo; inline.
            </p>
          ),
        },
        {
          slug: 'adding-households',
          title: 'Adding households',
          body: (
            <p>
              Admin &rarr; Manage Households &rarr; + Add household. Enter the address and click Save. The
              system creates a pending household entry waiting for someone to register.
            </p>
          ),
        },
        {
          slug: 'generating-invitation-codes',
          title: 'Generating invitation codes',
          body: (
            <p>
              On any pending household, click the generate button to produce a unique 6-character code.
              Residents enter this code at <code>portal.neighbortopia.app/signup</code> to join your
              neighborhood.
            </p>
          ),
        },
        {
          slug: 'emailing-invitations',
          title: 'Emailing invitations',
          body: (
            <p>
              Admin &rarr; Manage Households &rarr; Email Invitations button. Select the pending households
              you want to email, type each recipient&rsquo;s email, and click Send. The system logs the
              recipient and send time so you know who&rsquo;s been contacted.
            </p>
          ),
        },
        {
          slug: 'exporting-mailing-labels',
          title: 'Exporting mailing labels + postcards',
          body: (
            <p>
              Admin &rarr; Manage Households &rarr; Export mailing labels. Select households and click Export.
              The PDF includes a 4&times;6 postcard template on page 1 (NeighborTopia branding + how to
              register) and Avery 5160 label sheets with each household&rsquo;s address + code on the
              following pages. Print duplex, apply the label from the last sheet to the postcard, add a stamp,
              and mail.
            </p>
          ),
        },
        {
          slug: 'posting-announcement',
          title: 'Posting an announcement',
          body: (
            <p>
              Admin &rarr; Manage Announcements &rarr; + button. Give it a title, write the content, and
              optionally add a clickable link (click the link icon above the Content box). You can also toggle
              &ldquo;Add to Calendar&rdquo; to create a companion event, and uncheck &ldquo;Email all
              neighbors&rdquo; if you only want it to appear in-app.
            </p>
          ),
        },
        {
          slug: 'managing-discussions',
          title: 'Managing discussions',
          body: (
            <p>
              Admin &rarr; Manage Discussions. See every discussion in the neighborhood with reply counts.
              Click one to view / edit / delete it or any of its comments.
            </p>
          ),
        },
        {
          slug: 'flagged-content',
          title: 'Flagged content',
          body: (
            <p>
              Admin &rarr; Flagged Content. Review posts that neighbors have flagged. Approve (keep), remove,
              or ban the author. A red badge next to this section shows pending flags.
            </p>
          ),
        },
        {
          slug: 'categories',
          title: 'Categories',
          body: (
            <p>
              Admin &rarr; Manage Categories. Add or remove discussion categories (General, Marketplace, Movie
              Night, etc.) that neighbors can tag their posts with.
            </p>
          ),
        },
        {
          slug: 'creating-events',
          title: 'Creating events',
          body: (
            <p>
              Admin &rarr; Manage Events &rarr; + Add event. Enter title, description, start/end times, and
              location. Events show up on the Events tab for all neighbors.
            </p>
          ),
        },
        {
          slug: 'hoa-payment-tracker',
          title: 'HOA Payment Tracker',
          body: (
            <p>
              Admin &rarr; HOA Payment Tracker. Configure your fee amount, due dates, and fiscal year, then
              track payment status for every household. You can send email reminders (one-off or scheduled)
              and customize the reminder templates.
            </p>
          ),
        },
        {
          slug: 'subscription',
          title: 'Subscription',
          body: (
            <p>
              Admin &rarr; Subscription. See your current plan, next billing date, and update payment method.
              Super admins can also pause / cancel the subscription.
            </p>
          ),
        },
      ],
    }

    const faq = {
      key: 'faq',
      title: 'FAQ',
      sections: [
        {
          slug: 'faq-emails',
          title: 'Why am I not getting emails?',
          body: (
            <p>
              Check your spam folder first. If still missing, open your Profile &rarr; Notifications and make
              sure email notifications are on for the type of post. Admins can also check Admin &rarr; Flagged
              Content and the Mailgun delivery dashboard.
            </p>
          ),
        },
        {
          slug: 'faq-notifications',
          title: 'How do I change notification preferences?',
          body: (
            <p>
              Profile &rarr; Notifications. You can toggle emails for discussions, announcements, messages,
              and events independently.
            </p>
          ),
        },
        {
          slug: 'faq-hide-neighbor',
          title: 'How do I hide a neighbor without blocking them?',
          body: (
            <p>
              On any post, use the &middot;&middot;&middot; menu &rarr; Hide user. Hidden users still appear
              in the Directory but their posts are collapsed. Unlike blocking, they can still message you.
            </p>
          ),
        },
        {
          slug: 'faq-dark-mode',
          title: 'How does Dark Mode work?',
          body: (
            <p>
              Click the gear icon &rarr; Appearance. &ldquo;System&rdquo; follows your OS setting; Light and
              Dark force a specific theme.
            </p>
          ),
        },
        {
          slug: 'faq-archive-thread',
          title: 'How do I archive a message thread?',
          body: (
            <p>
              On the Messages page, each conversation row has a &middot;&middot;&middot; menu with Archive.
              Archived threads move to the Archived tab and no longer count toward your unread badge.
            </p>
          ),
        },
      ],
    }

    return isAdmin
      ? [gettingStarted, adminBucket, faq]
      : [gettingStarted, faq]
  }, [isAdmin])

  // Filter buckets by the current search query. Each section's title +
  // extracted body text is matched case-insensitively; buckets with no
  // matching sections drop out entirely so the modal looks tidy.
  const q = query.trim().toLowerCase()
  const filteredBuckets = useMemo(() => {
    if (!q) return buckets
    return buckets
      .map((b) => ({
        ...b,
        sections: b.sections.filter((s) => {
          const hay = `${s.title} ${extractText(s.body)}`.toLowerCase()
          return hay.includes(q)
        }),
      }))
      .filter((b) => b.sections.length > 0)
  }, [buckets, q])
  const totalMatches = filteredBuckets.reduce((n, b) => n + b.sections.length, 0)

  if (!isOpen) return null

  const handleTocClick = (e, slug) => {
    e.preventDefault()
    const target = document.getElementById(slug)
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  return (
    <div className="help-center-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-label="Help Center">
      <div className="help-center-modal" onClick={(e) => e.stopPropagation()}>
        <button
          className="help-center-close"
          onClick={onClose}
          aria-label="Close help center"
          title="Close"
        >
          <X size={20} />
        </button>

        <div className="help-center-header">
          <h2 className="help-center-title">Help Center</h2>
          <div className="help-center-search">
            <Search size={16} className="help-center-search-icon" aria-hidden="true" />
            <input
              type="search"
              className="help-center-search-input"
              placeholder="Search help topics…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Search help topics"
              autoFocus
            />
            {q && (
              <span className="help-center-search-count">
                {totalMatches} {totalMatches === 1 ? 'match' : 'matches'}
              </span>
            )}
          </div>
        </div>

        <div className="help-center-body">
        <aside className="help-center-toc" aria-label="Help Center table of contents">
          <div className="help-center-toc-inner">
            {filteredBuckets.length === 0 ? (
              <p className="help-center-toc-empty">No matching topics.</p>
            ) : (
              filteredBuckets.map((bucket) => (
                <nav key={bucket.key} className="help-center-toc-group" aria-label={bucket.title}>
                  <h4>{bucket.title}</h4>
                  <ul>
                    {bucket.sections.map((s) => (
                      <li key={s.slug}>
                        <a href={`#${s.slug}`} onClick={(e) => handleTocClick(e, s.slug)}>
                          {s.title}
                        </a>
                      </li>
                    ))}
                  </ul>
                </nav>
              ))
            )}
          </div>
        </aside>

        <div className="help-center-content">
          {filteredBuckets.length === 0 && (
            <p className="help-center-empty">No results for &ldquo;{query}&rdquo;. Try a different word.</p>
          )}
          {filteredBuckets.map((bucket) => (
            <div key={bucket.key} className="help-center-bucket">
              <h2 className="help-center-bucket-title">{bucket.title}</h2>
              {bucket.sections.map((s) => (
                <section key={s.slug} id={s.slug} className="help-center-section">
                  <h3>
                    {s.title}
                    {walkthroughs[s.slug] && (
                      <button
                        type="button"
                        className="help-center-walkthrough-btn"
                        onClick={() => {
                          onStartWalkthrough?.(s.slug)
                          onClose?.()
                        }}
                        aria-label={`Start walkthrough: ${s.title}`}
                        title="Start walkthrough"
                      >
                        <Play size={14} />
                        <span>Start walkthrough</span>
                      </button>
                    )}
                  </h3>
                  {s.body}
                </section>
              ))}
            </div>
          ))}
        </div>
        </div>
      </div>
    </div>
  )
}

export default HelpCenter
